import { useEffect, useState } from 'react';
import {
  CONSUMER_CMS_SHOPPING_CART,
  errorMock,
  handleTimer,
  IGenericErrorCMS,
  IGlobalConfigCMS,
  IPersonalInfoCmsMobile,
  IProduct,
  OTP_CHOICE,
  useCmsConfig,
  useGenerateOTP,
  useVerifyOTP,
} from '@vfit/consumer/data-access';
import { errorManager, ErrorService, getClickToCallWithKeyOrUrl, resetData, trackView } from '@vfit/shared/data-access';
import { API } from '@vfit/shared/data-access';
import { useQueryClient } from 'react-query';
import { organizeOtp, Otp } from '@vfit/consumer/components';
import { useCheckout } from '../../../iBuyMobile.context';
import { ICheckoutData } from '../../checkout.models';
import { getTrackError } from '../../checkout.utils';
import TaggingOtpCard from './otpCard.tagging';

const OtpCard = ({ handleOnChangeEnableGoNext, handleGoPrevSlide }: ICheckoutData) => {
  const queryClient = useQueryClient();
  const {
    currentStepKey,
    checkoutErrors,
    findCaller,
    isModalButtonSticky,
    product,
    timerID,
    setFindCaller,
    setIsDisabledBackButton,
    setTimerID,
  } = useCheckout();
  const {
    mutate: generateOtp,
    isError: isGenerateError,
    error: errorGenerateOtp,
  } = useGenerateOTP();
  const {
    mutate,
    data,
    isSuccess,
    isError: isErrorOtp,
    isLoading,
    error: verifyOtpErrorData,
  } = useVerifyOTP();

  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR_MOBILE
  ) as IGenericErrorCMS;
  const dataFromCms = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_PERSONAL_DATA_MOBILE
  ) as IPersonalInfoCmsMobile;

  const [isError, setIsError] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(false);

  const handleSendOtp = (field: string, fieldType: string) => {
    resetData(queryClient, ['generateOTP']);
    generateOtp({
      identifier: field,
      identifierType: fieldType,
    });
    if (
      dataFromCms?.personalinfomobile?.clientRecognition?.timer?.amount &&
      !isNaN(parseInt(dataFromCms?.personalinfomobile?.clientRecognition?.timer?.amount)) &&
      parseInt(dataFromCms?.personalinfomobile?.clientRecognition?.timer?.amount) > 0
    ) {
      clearInterval(timerID);
      setIsTimerActive(true);
      const timer = handleTimer(
        parseInt(dataFromCms?.personalinfomobile?.clientRecognition?.timer?.amount),
        () => setIsTimerActive(false)
      );
      setTimerID(timer);
    }
  };

  const handleVerify = (otp: string) => {
    mutate(otp);
  };

  const handleSetError = () => {
    setIsError(false);
  };

  const cmsGlobalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;

  const globalConfig = cmsGlobalConfig?.globalconfig;

  const numOfFailures = parseInt(localStorage.getItem('numOfOtpFailures') || '0', 10);

  useEffect(() => {
    if (!isGenerateError) {
      return;
    }

    if (numOfFailures < (globalConfig?.otp?.maxFailureNumber || 0)) {
      localStorage.setItem('numOfOtpFailures', (numOfFailures + 1).toString());
    } else {
      const otpError = globalConfig?.otp?.adHocError || {};

      const redirect = () => {
        window.open(otpError?.buttonLink || '', '_self');
      };
      errorManager.handleError(ErrorService.getSeverityErrorHigh(), {
        title: otpError?.title || '',
        message: otpError?.description || '',
        onClose: redirect,
        actionEvent: redirect,
        actionText: otpError?.buttonLabel || '',
        trackError: getTrackError(errorMock(`generate`, errorGenerateOtp || undefined)),
      });
    }
  }, [isGenerateError]);


  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem('numOfOtpFailures');
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  useEffect(() => {
    if (isErrorOtp || (!isErrorOtp && isSuccess && !data)) {
      setIsError(true);
      if (verifyOtpErrorData) {
        trackView({
          ...getTrackError(errorMock('VerifyOtp', verifyOtpErrorData), 'OTP', queryClient),
          page_error_code: verifyOtpErrorData.status || '',
        });
      }
    }
  }, [isSuccess, data, isErrorOtp, verifyOtpErrorData]);

  useEffect(() => {
    if (handleOnChangeEnableGoNext) {
      setFindCaller({ ...findCaller, isVerified: !!data, verifyOtp: (data as any)?.id || '' });
      localStorage.setItem(OTP_CHOICE, findCaller?.selectedContactMedium?.fieldType || '');
      resetData(queryClient, ['findCaller']);
      handleOnChangeEnableGoNext(!!data);
    }
  }, [data]);

  useEffect(() => {
    setIsDisabledBackButton(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (isGenerateError && handleGoPrevSlide) {
      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleGoPrevSlide,
        errorMock('generateOtp', errorGenerateOtp),
        undefined,
        undefined,
        handleGoPrevSlide,
        genericError?.genericerror?.buttonLabel || '',
        () =>
          getClickToCallWithKeyOrUrl(
            queryClient,
            product?.genericErrorCallMeNow?.url || '',
            product,
            () => handleGoPrevSlide(),
            currentStepKey,
            errorMock('generateOtp', errorGenerateOtp)
          ),
        product?.removeCTC ? undefined : product?.genericErrorCallMeNow?.label || '',
        isModalButtonSticky,
        true
      );
    }
  }, [isGenerateError]);

  useEffect(() => {
    if (isError) {
      setTimeout(() => {
        setIsError(false);
      }, 3000);
    }
  }, [isError]);

  useEffect(() => {
    if (findCaller.selectedContactMedium?.field)
      handleSendOtp(
        findCaller.selectedContactMedium.field,
        findCaller.selectedContactMedium.fieldType
      );
  }, []);

  return (
    <>
      <TaggingOtpCard />
      <Otp
        product={product as IProduct}
        contactMedium={findCaller.selectedContactMedium}
        isError={isError}
        isLoading={isLoading}
        reSendOtp={handleSendOtp}
        onVerify={handleVerify}
        setIsError={handleSetError}
        isTimerActive={isTimerActive}
        configOtp={organizeOtp(
          dataFromCms?.personalinfomobile,
          findCaller?.selectedContactMedium?.fieldType === 'msisdn',
          findCaller?.selectedContactMedium
        )}
      />
    </>
  );
};

export default OtpCard;

